import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  CardActions,
} from '@mui/material';
import { useApiGet, useApiSend } from '../../utils/httpClient';
import {
  getUserNotes,
  getUserNoteTypes,
  updateUserNote,
  createUserNote,
  deleteUserNote,
} from '../../endpoints/usersTs';
import dayjs from 'dayjs';
import { Add, Cancel, Delete, Edit, Save } from '@mui/icons-material';

export default function UserNotes({ id }) {
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('');
  const [noteId, setNoteId] = useState(null);
  const [addNote, setAddNote] = useState(false);

  const resetState = () => {
    setIsSaving(false);
    setErrorSaving(null);
    setNoteText('');
    setNoteType('');
    setNoteId(null);
    setAddNote(false);
  };

  const {
    data: userNotes,
    isLoading,
    refetch,
  } = useApiGet(
    ['userNotes', id],
    getUserNotes,
    {
      enabled: !!id,
    },
    { id }
  );

  const { data: noteTypes } = useApiGet(
    ['noteTypes'],
    getUserNoteTypes,
    {
      enabled: !!id,
    },
    { id }
  );
  const { mutate: updateNote } = useApiSend(
    updateUserNote,
    (data) => {
      resetState();
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: createNote } = useApiSend(
    createUserNote,
    (data) => {
      resetState();
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );
  const { mutate: deleteNote } = useApiSend(
    deleteUserNote,
    (data) => {
      resetState();
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );
  const handleUpdateNote = (noteId, noteData) => {
    setIsSaving(true);
    updateNote({ noteId: noteId, data: noteData });
  };
  const handleCreateNote = (noteData) => {
    setIsSaving(true);
    createNote({ data: noteData, userId: id });
  };
  const handleDeleteNote = (noteId) => {
    setIsSaving(true);
    deleteNote({ noteId: noteId, userId: id });
  };

  const handleEditNote = (note) => {
    setNoteText(note.note);
    setNoteType(note.note_type_id);
    setNoteId(note.id);
  };

  const handleAddNote = () => {
    setAddNote(!addNote);
    setNoteText('');
    setNoteType('');
    setNoteId(null);
  };

  const handleSaveNote = () => {
    if (!noteText || !noteType) {
      setErrorSaving('Please fill in all fields');
      return;
    }
    if (noteId) {
      handleUpdateNote(noteId, { note: noteText, note_type: noteType });
    } else {
      handleCreateNote({ note: noteText, note_type: noteType });
    }
    setAddNote(false);
  };

  const handleCancel = () => {
    setAddNote(false);
    setNoteText('');
    setNoteType('');
    setNoteId(null);
    setErrorSaving(null);
  };
  const handleDelete = (noteId) => {
    handleDeleteNote(noteId);
  };
  const handleNoteTypeChange = (event) => {
    setNoteType(event.target.value);
  };
  const handleNoteTextChange = (event) => {
    // max length of 500
    if (event.target.value.length <= 500) {
      setNoteText(event.target.value);
    }
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid item xs={12} padding>
          <Typography variant="h6">Loading...</Typography>
        </Grid>
      </Grid>
    );
  }
  if (!userNotes) {
    return (
      <Grid container>
        <Grid item xs={12} padding>
          <Typography variant="h6">Notes not found</Typography>
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container>
      <Grid item xs={12} padding textAlign="center">
        <Button
          variant="contained"
          color={addNote ? 'warning' : 'green'}
          onClick={addNote ? handleCancel : handleAddNote}
          disabled={isSaving || noteId}
          size="small"
          sx={{ fontWeight: 'bold' }}
        >
          {addNote ? (
            <Cancel sx={{ paddingRight: 1 }} />
          ) : (
            <Add sx={{ paddingRight: 1 }} />
          )}
          {addNote ? 'Cancel' : 'Add'}
        </Button>
        {addNote && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSaveNote}
            disabled={isSaving || noteId}
            sx={{ fontWeight: 'bold', marginLeft: 2 }}
          >
            <Save sx={{ paddingRight: 1 }} />
            Save
          </Button>
        )}
      </Grid>
      {errorSaving && (
        <Grid item xs={12} padding textAlign="center">
          <Typography variant="body1" color="error">
            {errorSaving}
          </Typography>
        </Grid>
      )}
      {addNote && (
        <Grid item container xs={12}>
          <Grid item xs={12} padding>
            <FormControl fullWidth>
              <InputLabel id="note-type-label">Note Type</InputLabel>
              <Select
                labelId="note-type-label"
                id="note-type-select"
                label="Note Type"
                value={noteType}
                onChange={handleNoteTypeChange}
                fullWidth
                disabled={isSaving}
                sx={{ marginBottom: 2 }}
              >
                {noteTypes?.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} padding>
            <TextField
              label="Note"
              multiline
              rows={4}
              value={noteText}
              onChange={handleNoteTextChange}
              fullWidth
              variant="outlined"
              disabled={isSaving}
              sx={{ marginBottom: 2 }}
            />
          </Grid>
        </Grid>
      )}
      {userNotes.map((note) => (
        <Grid item xs={12} md={6} padding key={note.id}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <Grid container justifyContent={'space-between'}>
                  <Grid item xs={10}>
                    {note.id === noteId ? 'Editing Note' : note.note_type}
                  </Grid>
                  {noteId === null && (
                    <Grid item xs={2} textAlign={'right'}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditNote(note)}
                        disabled={isSaving}
                        color="blue"
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(note.id)}
                        disabled={isSaving}
                        color="error"
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              }
              subheader={
                note.id === noteId
                  ? null
                  : `Created on: ${dayjs(note.created_at).format('MM/DD/YYYY')} - By: ${note.created_by}`
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {note.id === noteId ? (
                <Grid item container xs={12}>
                  <Grid item xs={12} padding>
                    <FormControl fullWidth>
                      <InputLabel id="note-type-label">Note Type</InputLabel>
                      <Select
                        labelId="note-type-label"
                        id="note-type-select"
                        label="Note Type"
                        value={noteType}
                        onChange={handleNoteTypeChange}
                        fullWidth
                        disabled={isSaving}
                        sx={{ marginBottom: 2 }}
                      >
                        {noteTypes?.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} padding>
                    <TextField
                      label="Note"
                      multiline
                      rows={4}
                      value={noteText}
                      onChange={handleNoteTextChange}
                      fullWidth
                      variant="outlined"
                      disabled={isSaving}
                      sx={{ marginBottom: 2 }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body1" component={'span'}>
                  {note.note}
                </Typography>
              )}
            </CardContent>
            {note.id === noteId && (
              <CardActions>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={handleCancel}
                  disabled={isSaving}
                  sx={{ fontWeight: 'bold', marginLeft: 2 }}
                >
                  <Cancel sx={{ paddingRight: 1 }} />
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSaveNote}
                  disabled={isSaving}
                  sx={{ fontWeight: 'bold', marginLeft: 2 }}
                >
                  <Save sx={{ paddingRight: 1 }} />
                  Save
                </Button>
              </CardActions>
            )}
          </Card>
        </Grid>
      ))}
      {userNotes.length === 0 && (
        <Grid item xs={12} padding textAlign="center">
          <Typography variant="body1">No notes available</Typography>
        </Grid>
      )}
    </Grid>
  );
}
